import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

export const ACTIVE_COMPANY_KEY = 'activeCompanyKey';

export interface CompanyContextProps {
  activeCompanyKey: string | null;
  availableCompanyKeys: string[] | null;
  needsCompanySelection: boolean;
  selectCompanyKey: (key: string) => void;
  clearCompanySelection: () => void;
  setAvailableCompanyKeys: (keys: string[]) => void;
}

export const CompanyContext = createContext<CompanyContextProps | undefined>(
  undefined
);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export const CompanyProvider: React.FC = ({ children }) => {
  const [availableCompanyKeys, setAvailableCompanyKeys] = useState<
    string[] | null
  >(null);

  const [activeCompanyKey, setActiveCompanyKey] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_COMPANY_KEY)
  );

  const selectCompanyKey = useCallback((key: string) => {
    if (!key) {
      return;
    }

    setActiveCompanyKey(key);
    localStorage.setItem(ACTIVE_COMPANY_KEY, key);
  }, []);

  const clearCompanySelection = useCallback(() => {
    setActiveCompanyKey(null);
    localStorage.removeItem(ACTIVE_COMPANY_KEY);
  }, []);

  useEffect(() => {
    if (availableCompanyKeys === null) {
      return;
    }

    if (availableCompanyKeys?.length === 0) {
      setActiveCompanyKey(null);
      localStorage.removeItem(ACTIVE_COMPANY_KEY);
      return;
    }

    if (availableCompanyKeys?.length === 1) {
      const [singleKey] = availableCompanyKeys;

      setActiveCompanyKey(singleKey);
      localStorage.setItem(ACTIVE_COMPANY_KEY, singleKey);

      return;
    }

    if (activeCompanyKey && availableCompanyKeys?.includes(activeCompanyKey)) {
      return;
    }

    setActiveCompanyKey(null);
    localStorage.removeItem(ACTIVE_COMPANY_KEY);
  }, [availableCompanyKeys, activeCompanyKey]);

  const needsCompanySelection =
    availableCompanyKeys != null &&
    availableCompanyKeys.length > 1 &&
    !activeCompanyKey;

  const value = {
    activeCompanyKey,
    availableCompanyKeys,
    needsCompanySelection,
    selectCompanyKey,
    clearCompanySelection,
    setAvailableCompanyKeys,
  } satisfies CompanyContextProps;

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
};
